import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/homeData";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          isScrolled || mobileMenuOpen
            ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/40"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-14 md:h-16">
            <div className="flex items-center gap-4 md:gap-6">
              <Link to="/" className="flex items-center gap-2 cursor-pointer">
                <span className="font-heading text-2xl md:text-3xl font-bold text-accent-500 tracking-tight">
                  ItaoBuy
                </span>
              </Link>

              <div className="hidden md:flex items-center gap-4">
                <Link
                  to="/"
                  className="text-sm font-medium text-foreground-700 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Home
                </Link>
                <Link
                  to="/articoli/guida-acquisti-itaobuy"
                  className="text-sm font-medium text-foreground-700 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Guida Acquisti
                </Link>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-4">
              <div className="relative hidden md:block" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-accent-600"
                >
                  <span>Esplora Categorie</span>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
                  </span>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 rounded-xl border border-background-200/70 overflow-hidden max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <img src={cat.image} alt={cat.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className={`${cat.icon} text-xs text-accent-500`}></i>
                        </span>
                        <span>{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:flex items-center gap-1.5 px-4 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-accent-600"
              >
                itaobuy sheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </span>
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-primary-600"
              >
                buy it now
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </span>
              </a>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 ${
                  mobileMenuOpen
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-100/90 text-foreground-700"
                }`}
                aria-label="Menu"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`text-lg ${mobileMenuOpen ? "ri-close-line" : "ri-menu-3-line"}`}></i>
                </span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[90]">
          <div
            className="absolute inset-0 top-14 bg-foreground-950/40"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute top-14 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto border-t border-background-200/50">
            <div className="px-5 py-5 flex flex-col gap-4">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 text-base font-semibold text-foreground-900 cursor-pointer py-2"
              >
                <span className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-full">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-home-4-line text-accent-500"></i>
                  </span>
                </span>
                Home
              </Link>

              <Link
                to="/articoli/guida-acquisti-itaobuy"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 text-base font-semibold text-foreground-900 cursor-pointer py-2"
              >
                <span className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-full">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shopping-cart-2-line text-accent-500"></i>
                  </span>
                </span>
                Guida Acquisti
              </Link>

              <div className="border-t border-background-200/50 pt-4">
                <button
                  onClick={() => setMobileCatOpen(!mobileCatOpen)}
                  className="flex items-center justify-between w-full text-base font-semibold text-foreground-900 cursor-pointer py-2"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-full">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-grid-line text-accent-500"></i>
                      </span>
                    </span>
                    Esplora Categorie
                  </div>
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-lg text-accent-500 transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
                  </span>
                </button>
                {mobileCatOpen && (
                  <div className="mt-3 grid grid-cols-2 gap-2.5">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2.5 p-2.5 bg-background-100 rounded-xl cursor-pointer transition-colors hover:bg-accent-50 border border-background-200/30"
                      >
                        <img src={cat.image} alt={cat.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                        <span className="text-xs font-medium text-foreground-700 leading-tight">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3 pt-4 border-t border-background-200/50">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-accent-500 text-background-50 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  itaobuy sheet
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap"
                >
                  buy it now
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}